import DeviceGroupsDataService from '@/services/DeviceGroupsDataService'
import { buildExpiryFields, buildPayloadForCommand, normalizeCatalog } from '@/util/commandCatalog'

// Shared plumbing for the Devices dialogs that can send their command either to
// the selected devices or, instead, to a whole device group through
// POST /api/device-groups/{groupId}/commands.
export default {
  data () {
    return {
      deviceGroups: [],
      loadingDeviceGroups: false,
      groupCommandSending: false,
      groupCommandCatalog: null,
    }
  },

  methods: {
    async loadDeviceGroups (force = false) {
      if (this.deviceGroups.length && !force) {
        return this.deviceGroups
      }

      this.loadingDeviceGroups = true
      try {
        const response = await DeviceGroupsDataService.getAll()
        this.deviceGroups = Array.isArray(response.data) ? response.data : []
      } catch (error) {
        console.error('Error loading device groups:', error)
        this.deviceGroups = []
      } finally {
        this.loadingDeviceGroups = false
      }

      return this.deviceGroups
    },

    async loadGroupCommandCatalog () {
      if (this.groupCommandCatalog) {
        return this.groupCommandCatalog
      }

      try {
        const response = await DeviceGroupsDataService.getCommandTypes()
        this.groupCommandCatalog = normalizeCatalog(response.data)
      } catch (error) {
        console.error('Error loading command catalog:', error)
        this.groupCommandCatalog = normalizeCatalog(null)
      }

      return this.groupCommandCatalog
    },

    deviceGroupName (groupId) {
      const group = this.deviceGroups.find(entry => entry.id === groupId)
      return group ? group.name : `Group #${groupId}`
    },

    // Returns true when the batch was accepted, so callers can close their dialog.
    async sendGroupCommand (groupId, type, payload = null, expiry = null) {
      if (!groupId) {
        this.$toast.error('Select a device group.', 'Error', { position: 'topRight' })
        return false
      }

      this.groupCommandSending = true
      try {
        // The dialogs hand us loosely-keyed values; the catalog says what the
        // command's payload keys actually are, so map onto those when we can.
        const catalog = await this.loadGroupCommandCatalog()
        const command = catalog.commandsByType[type]
        const body = {
          type,
          payload: command
            ? buildPayloadForCommand(command, payload)
            : (payload === undefined ? null : payload),
          // ttlMinutes or expiresAt, or neither for the per-type default.
          ...buildExpiryFields(expiry),
        }

        const response = await DeviceGroupsDataService.sendCommand(groupId, body)
        const result = response.data || {}
        const targetCount = result.targetCount === undefined ? null : result.targetCount

        this.$toast.success(
          targetCount === null
            ? `Command sent to ${this.deviceGroupName(groupId)}.`
            : `Command sent to ${targetCount} device${targetCount === 1 ? '' : 's'} in ${this.deviceGroupName(groupId)}.`,
          'Success',
          { position: 'topRight' },
        )

        if (result.failedCount) {
          this.$toast.error(
            `${result.failedCount} device${result.failedCount === 1 ? '' : 's'} failed to receive the command.`,
            'Error',
            { position: 'topRight' },
          )
        }

        return true
      } catch (error) {
        const status = error && error.response ? error.response.status : null
        const message = error && error.response && error.response.data
          ? (error.response.data.message || error.response.data.title)
          : null

        this.$toast.error(
          status === 403
            ? 'You are not allowed to send commands to this group.'
            : (message || 'Failed to send the group command.'),
          'Error',
          { position: 'topRight' },
        )
        return false
      } finally {
        this.groupCommandSending = false
      }
    },
  },
}
